import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from './config';
import { dogService } from './dogService';
import { MatingRecord, MatingRecordFormData } from '@/types/dog';

/**
 * 交配記録検索フィルタ
 */
export interface MatingRecordSearchFilter {
  maleId?: string;
  femaleId?: string;
  matingDateFrom?: Date;
  matingDateTo?: Date;
  hasLinkedBirthRecord?: boolean; // 出産記録が紐付いているか
}

/**
 * 交配記録管理サービスクラス
 */
export class MatingRecordService {
  private readonly COLLECTION_NAME = 'matingRecords';
  private readonly GESTATION_PERIOD_DAYS = 63; // 犬の平均妊娠期間（日）

  /**
   * 新規交配記録作成
   */
  async createMatingRecord(data: MatingRecordFormData, userId: string): Promise<MatingRecord> {
    try {
      // 親犬の所有確認と性別確認
      await this.verifyParentOwnership(data.maleId, data.femaleId, userId);

      // ドキュメントIDを生成
      const recordRef = doc(collection(db, this.COLLECTION_NAME));
      const recordId = recordRef.id;

      // 出産予定日を計算
      const expectedBirthDate = this.calculateExpectedBirthDate(data.matingDate);

      const now = Timestamp.now();

      const matingRecord: MatingRecord = {
        id: recordId,
        ownerId: userId,
        maleId: data.maleId,
        femaleId: data.femaleId,
        matingDate: Timestamp.fromDate(data.matingDate),
        expectedBirthDate: Timestamp.fromDate(expectedBirthDate),
        createdAt: now,
      };

      await setDoc(recordRef, matingRecord);

      return matingRecord;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`交配記録の作成に失敗しました: ${error.message}`);
      }
      throw new Error('交配記録の作成に失敗しました');
    }
  }

  /**
   * 交配記録取得（単体）
   */
  async getMatingRecord(recordId: string, userId: string): Promise<MatingRecord | null> {
    try {
      const recordRef = doc(db, this.COLLECTION_NAME, recordId);
      const recordSnap = await getDoc(recordRef);

      if (!recordSnap.exists()) {
        return null;
      }

      const matingRecord = recordSnap.data() as MatingRecord;

      // 自身の記録のみアクセス可能
      if (matingRecord.ownerId !== userId) {
        throw new Error('この交配記録にアクセスする権限がありません');
      }

      return matingRecord;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`交配記録の取得に失敗しました: ${error.message}`);
      }
      throw new Error('交配記録の取得に失敗しました');
    }
  }

  /**
   * 交配記録一覧取得（ソート対応）
   */
  async getMatingRecords(
    userId: string,
    sortBy: 'matingDate' | 'expectedBirthDate' | 'createdAt' = 'matingDate',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<MatingRecord[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('ownerId', '==', userId),
        orderBy(sortBy, sortOrder),
      ];

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const querySnapshot = await getDocs(q);

      const matingRecords: MatingRecord[] = [];
      querySnapshot.forEach((doc) => {
        matingRecords.push(doc.data() as MatingRecord);
      });

      return matingRecords;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`交配記録一覧の取得に失敗しました: ${error.message}`);
      }
      throw new Error('交配記録一覧の取得に失敗しました');
    }
  }

  /**
   * オス犬の交配履歴取得
   */
  async getMatingRecordsByMale(maleId: string, userId: string): Promise<MatingRecord[]> {
    try {
      // オス犬の所有確認
      const male = await dogService.getDog(maleId, userId);
      if (!male) {
        throw new Error('オス犬が見つかりません');
      }

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('ownerId', '==', userId),
        where('maleId', '==', maleId),
        orderBy('matingDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const matingRecords: MatingRecord[] = [];

      querySnapshot.forEach((doc) => {
        matingRecords.push(doc.data() as MatingRecord);
      });

      return matingRecords;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`オス犬の交配履歴の取得に失敗しました: ${error.message}`);
      }
      throw new Error('オス犬の交配履歴の取得に失敗しました');
    }
  }

  /**
   * メス犬の交配履歴取得
   */
  async getMatingRecordsByFemale(femaleId: string, userId: string): Promise<MatingRecord[]> {
    try {
      // メス犬の所有確認
      const female = await dogService.getDog(femaleId, userId);
      if (!female) {
        throw new Error('メス犬が見つかりません');
      }

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('ownerId', '==', userId),
        where('femaleId', '==', femaleId),
        orderBy('matingDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const matingRecords: MatingRecord[] = [];

      querySnapshot.forEach((doc) => {
        matingRecords.push(doc.data() as MatingRecord);
      });

      return matingRecords;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`メス犬の交配履歴の取得に失敗しました: ${error.message}`);
      }
      throw new Error('メス犬の交配履歴の取得に失敗しました');
    }
  }

  /**
   * 交配記録更新
   */
  async updateMatingRecord(
    recordId: string,
    data: Partial<MatingRecordFormData>,
    userId: string
  ): Promise<void> {
    try {
      // 存在確認と権限チェック
      const existingRecord = await this.getMatingRecord(recordId, userId);
      if (!existingRecord) {
        throw new Error('交配記録が見つかりません');
      }

      // 親犬が変更された場合は所有確認
      if (data.maleId || data.femaleId) {
        await this.verifyParentOwnership(
          data.maleId || existingRecord.maleId,
          data.femaleId || existingRecord.femaleId,
          userId
        );
      }

      const recordRef = doc(db, this.COLLECTION_NAME, recordId);

      const updatePayload: Record<string, unknown> = {};

      if (data.maleId) updatePayload.maleId = data.maleId;
      if (data.femaleId) updatePayload.femaleId = data.femaleId;

      // 交配日が変更された場合は出産予定日も再計算
      if (data.matingDate) {
        updatePayload.matingDate = Timestamp.fromDate(data.matingDate);
        updatePayload.expectedBirthDate = Timestamp.fromDate(
          this.calculateExpectedBirthDate(data.matingDate)
        );
      }

      await updateDoc(recordRef, updatePayload);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`交配記録の更新に失敗しました: ${error.message}`);
      }
      throw new Error('交配記録の更新に失敗しました');
    }
  }

  /**
   * 交配記録削除
   */
  async deleteMatingRecord(recordId: string, userId: string): Promise<void> {
    try {
      // 存在確認と権限チェック
      const existingRecord = await this.getMatingRecord(recordId, userId);
      if (!existingRecord) {
        throw new Error('交配記録が見つかりません');
      }

      const recordRef = doc(db, this.COLLECTION_NAME, recordId);
      await deleteDoc(recordRef);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`交配記録の削除に失敗しました: ${error.message}`);
      }
      throw new Error('交配記録の削除に失敗しました');
    }
  }

  /**
   * 出産記録との紐付け
   */
  async linkBirthRecord(recordId: string, birthRecordId: string, userId: string): Promise<void> {
    try {
      // 交配記録の存在確認と権限チェック
      const existingRecord = await this.getMatingRecord(recordId, userId);
      if (!existingRecord) {
        throw new Error('交配記録が見つかりません');
      }

      const recordRef = doc(db, this.COLLECTION_NAME, recordId);
      await updateDoc(recordRef, { birthRecordId });
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`出産記録の紐付けに失敗しました: ${error.message}`);
      }
      throw new Error('出産記録の紐付けに失敗しました');
    }
  }

  /**
   * 交配記録検索（高度なフィルタリング）
   */
  async searchMatingRecords(
    userId: string,
    filter: MatingRecordSearchFilter
  ): Promise<MatingRecord[]> {
    try {
      // まず全記録を取得
      let matingRecords = await this.getMatingRecords(userId);

      // クライアント側でフィルタリング
      if (filter.maleId) {
        matingRecords = matingRecords.filter((record) => record.maleId === filter.maleId);
      }

      if (filter.femaleId) {
        matingRecords = matingRecords.filter((record) => record.femaleId === filter.femaleId);
      }

      if (filter.matingDateFrom) {
        const fromTimestamp = Timestamp.fromDate(filter.matingDateFrom);
        matingRecords = matingRecords.filter(
          (record) => record.matingDate.toMillis() >= fromTimestamp.toMillis()
        );
      }

      if (filter.matingDateTo) {
        const toTimestamp = Timestamp.fromDate(filter.matingDateTo);
        matingRecords = matingRecords.filter(
          (record) => record.matingDate.toMillis() <= toTimestamp.toMillis()
        );
      }

      if (filter.hasLinkedBirthRecord !== undefined) {
        matingRecords = matingRecords.filter((record) =>
          filter.hasLinkedBirthRecord ? !!record.birthRecordId : !record.birthRecordId
        );
      }

      return matingRecords;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`交配記録検索に失敗しました: ${error.message}`);
      }
      throw new Error('交配記録検索に失敗しました');
    }
  }

  /**
   * 出産予定日計算（交配日から）
   */
  private calculateExpectedBirthDate(matingDate: Date): Date {
    const expectedDate = new Date(matingDate);
    expectedDate.setDate(expectedDate.getDate() + this.GESTATION_PERIOD_DAYS);
    return expectedDate;
  }

  /**
   * 親犬の所有確認
   */
  private async verifyParentOwnership(
    maleId: string,
    femaleId: string,
    userId: string
  ): Promise<void> {
    const male = await dogService.getDog(maleId, userId);
    const female = await dogService.getDog(femaleId, userId);

    if (!male) {
      throw new Error('オス犬が見つかりません');
    }

    if (!female) {
      throw new Error('メス犬が見つかりません');
    }

    // 性別確認
    if (male.gender !== 'male') {
      throw new Error('選択した犬はオスではありません');
    }

    if (female.gender !== 'female') {
      throw new Error('選択した犬はメスではありません');
    }
  }
}

// シングルトンインスタンスをエクスポート
export const matingRecordService = new MatingRecordService();
